import {
  BoxGeometry,
  Group,
  InstancedMesh,
  Matrix4,
  Mesh,
  MeshStandardMaterial,
  Color,
} from 'three';
import type {
  DrivewayConfig,
  FenceConfig,
  FenceStyle,
  LotConfig,
} from '@/model/schema';
import type { MaterialKit } from '../materials';
import type { HingeDef } from '../hinges';
import type { HingeCollector } from './buildWalls';

/**
 * Fence enclosing all four sides of the lot, with a gate gap on the front
 * and an additional gap where a driveway crosses (so cars don't have to
 * drive through the fence — user reported the fence sealing the driveway
 * exit).
 *
 * Styles
 * ------
 *  - 'rail'   : posts + two horizontal rails (the v1 default).
 *  - 'picket' : posts + rails + dense vertical pickets between every pair
 *               of posts (a classic suburban picket fence).
 *  - 'slat'   : sparse posts + densely packed horizontal slats.
 *  - 'hedge'  : a continuous low rounded box of greenery, no posts at all.
 *
 * All styles take the same per-side gap list and produce the same number
 * of fence segments.
 */
export function buildFence(
  lot: LotConfig,
  fence: FenceConfig,
  kit: MaterialKit,
  hinges: HingeCollector,
  driveway?: DrivewayConfig,
): Group {
  const group = new Group();
  group.name = `Fence_${fence.style}`;

  const halfW = lot.width / 2 - fence.inset;
  const halfD = lot.depth / 2 - fence.inset;
  const gateWidth = 1.0;
  const gateCenterX = -lot.width / 2 + fence.gateOffset + gateWidth / 2;
  const drivePad = 0.15;
  const gateFrontZ = -halfD;

  // Per-side gap lists. Front always carries the entrance gate; any
  // driveway adds a gap on its `side`. We then slice each fence side
  // around its own merged gap list.
  const sideGaps: Record<'front' | 'back' | 'left' | 'right', Array<[number, number]>> = {
    front: [[gateCenterX - gateWidth / 2, gateCenterX + gateWidth / 2]],
    back: [],
    left: [],
    right: [],
  };
  if (driveway) {
    if (driveway.side === 'front' || driveway.side === 'back') {
      sideGaps[driveway.side].push([
        driveway.xLo - drivePad,
        driveway.xHi + drivePad,
      ]);
    } else {
      sideGaps[driveway.side].push([
        driveway.zLo - drivePad,
        driveway.zHi + drivePad,
      ]);
    }
  }

  interface Segment { axis: 'x' | 'z'; lo: number; hi: number; fixed: number; }
  const segments: Segment[] = [
    ...sliceLine('x', -halfW, halfW, -halfD, sideGaps.front),
    ...sliceLine('x', -halfW, halfW, halfD, sideGaps.back),
    ...sliceLine('z', -halfD, halfD, -halfW, sideGaps.left),
    ...sliceLine('z', -halfD, halfD, halfW, sideGaps.right),
  ].filter((s) => s.hi - s.lo > 0.05);

  if (fence.style === 'hedge') {
    buildHedge(group, segments, fence.height);
  } else {
    buildPostedFence(group, segments, fence, kit, fence.style);
  }

  // Entrance gate: a hinged panel filling the front gap. Lives in its
  // own `Hinge_gate-front` pivot so the same slider UX that drives
  // doors/windows can also swing the gate. Default half-open into the
  // lot so the walkway reads as "come on in".
  addFrontGate(group, fence, kit, hinges, gateCenterX, gateWidth, gateFrontZ);

  return group;
}

/**
 * Gate panel sized to fit the front gap, hinged on the left-hand post
 * (hinge sits at the WEST edge of the opening). Panel extends from the
 * pivot in +X so the natural "closed" state is parallel to the fence
 * line. Negative Y rotation swings the free edge toward +Z — i.e.
 * INTO the lot — which is what the user asked for.
 */
function addFrontGate(
  parent: Group,
  fence: FenceConfig,
  kit: MaterialKit,
  hinges: HingeCollector,
  gateCenterX: number,
  gateWidth: number,
  gateZ: number,
) {
  const sideGap = 0.03;
  const panelW = Math.max(0.5, gateWidth - 2 * sideGap);
  const panelH = fence.height * 0.92;
  const panelT = 0.05;

  const pivot = new Group();
  const def: HingeDef = {
    id: 'gate-front',
    label: 'Front Gate',
    category: 'gate',
    axis: 'Y',
    // -90° = fully open INTO the lot (free edge swings to +Z).
    // 0°  = closed (panel parallel to fence line along +X).
    // openSignFromMin=1: t=0 → maxDeg (closed), t=1 → minDeg (open).
    minDeg: -90,
    maxDeg: 0,
    openSignFromMin: 1,
    defaultT: 0.5,
  };
  pivot.name = `Hinge_${def.id}`;
  pivot.userData.hinge = def;
  pivot.position.set(gateCenterX - gateWidth / 2 + sideGap, 0, gateZ);
  hinges.add(def);

  const panel = new Mesh(new BoxGeometry(panelW, panelH, panelT), kit.fence);
  panel.name = 'GatePanel';
  panel.castShadow = true;
  panel.receiveShadow = true;
  panel.position.set(panelW / 2, panelH / 2, 0);
  pivot.add(panel);

  // Two thin horizontal rails so the gate reads as fabricated rather
  // than a solid plank — matches the look of a rail/picket fence. For
  // a hedge fence this still reads fine as a small wooden garden gate
  // set into the hedge opening.
  const railH = 0.05;
  for (const yFrac of [0.25, 0.75]) {
    const rail = new Mesh(
      new BoxGeometry(panelW - 0.04, railH, panelT + 0.02),
      kit.fence,
    );
    rail.name = `GateRail_${Math.round(yFrac * 100)}`;
    rail.castShadow = true;
    rail.position.set(panelW / 2, panelH * yFrac, 0);
    pivot.add(rail);
  }

  parent.add(pivot);
}

/** Slice one fence run (between `lo`..`hi` along `axis`, at the given
 *  perpendicular `fixed` coordinate) around N gaps. Overlapping gaps are
 *  merged, then we emit one segment per surviving stretch. */
function sliceLine(
  axis: 'x' | 'z',
  lo: number,
  hi: number,
  fixed: number,
  rawGaps: Array<[number, number]>,
): Array<{ axis: 'x' | 'z'; lo: number; hi: number; fixed: number }> {
  const merged = mergeIntervals(
    rawGaps
      .map(([a, b]) => [Math.max(lo, Math.min(hi, a)), Math.max(lo, Math.min(hi, b))] as [number, number])
      .filter(([a, b]) => b - a > 0.01),
  );
  const out: Array<{ axis: 'x' | 'z'; lo: number; hi: number; fixed: number }> = [];
  if (merged.length === 0) {
    out.push({ axis, lo, hi, fixed });
    return out;
  }
  let cursor = lo;
  for (const [a, b] of merged) {
    if (a > cursor) out.push({ axis, lo: cursor, hi: a, fixed });
    cursor = Math.max(cursor, b);
  }
  if (cursor < hi) out.push({ axis, lo: cursor, hi, fixed });
  return out;
}

function mergeIntervals(intervals: Array<[number, number]>): Array<[number, number]> {
  const sorted = intervals.slice().sort((a, b) => a[0] - b[0]);
  const out: Array<[number, number]> = [];
  for (const [a, b] of sorted) {
    const last = out[out.length - 1];
    if (last && a <= last[1]) {
      last[1] = Math.max(last[1], b);
    } else {
      out.push([a, b]);
    }
  }
  return out;
}

function buildPostedFence(
  group: Group,
  segments: Array<{ axis: 'x' | 'z'; lo: number; hi: number; fixed: number }>,
  fence: FenceConfig,
  kit: MaterialKit,
  style: Exclude<FenceStyle, 'hedge'>,
) {
  const postSize = 0.12;
  const postHeight = fence.height;

  // Posts: collect all positions first, dedupe, then instance them.
  const positions: Array<[number, number]> = [];
  for (const seg of segments) {
    // End-cap posts so each rail run has something to butt against.
    if (seg.axis === 'x') {
      positions.push([seg.lo, seg.fixed]);
      positions.push([seg.hi, seg.fixed]);
      // Spaced posts in between.
      for (let x = seg.lo + fence.postSpacing; x < seg.hi - 1e-3; x += fence.postSpacing) {
        positions.push([x, seg.fixed]);
      }
    } else {
      positions.push([seg.fixed, seg.lo]);
      positions.push([seg.fixed, seg.hi]);
      for (let z = seg.lo + fence.postSpacing; z < seg.hi - 1e-3; z += fence.postSpacing) {
        positions.push([seg.fixed, z]);
      }
    }
  }
  const dedup = dedupePositions(positions);

  const postGeo = new BoxGeometry(postSize, postHeight, postSize);
  const posts = new InstancedMesh(postGeo, kit.fence, dedup.length);
  posts.name = 'FencePosts';
  const m = new Matrix4();
  dedup.forEach(([x, z], i) => {
    m.makeTranslation(x, postHeight / 2, z);
    posts.setMatrixAt(i, m);
  });
  posts.count = dedup.length;
  posts.instanceMatrix.needsUpdate = true;
  group.add(posts);

  // Per-segment infill.
  for (const seg of segments) {
    addSegmentInfill(group, seg, fence, kit, style, postSize);
  }
}

function addSegmentInfill(
  group: Group,
  seg: { axis: 'x' | 'z'; lo: number; hi: number; fixed: number },
  fence: FenceConfig,
  kit: MaterialKit,
  style: Exclude<FenceStyle, 'hedge'>,
  postSize: number,
) {
  const segLen = seg.hi - seg.lo;
  if (segLen <= postSize) return;

  const railThickness = 0.03;
  const railHeight = 0.06;

  const addBox = (x: number, y: number, z: number, sx: number, sy: number, sz: number) => {
    const geo = new BoxGeometry(sx, sy, sz);
    const mesh = new Mesh(geo, kit.fence);
    mesh.position.set(x, y, z);
    mesh.castShadow = true;
    group.add(mesh);
  };

  const horiz = seg.axis === 'x';
  const center = (seg.lo + seg.hi) / 2;

  if (style === 'rail' || style === 'picket') {
    const railYs = [fence.height * 0.25, fence.height * 0.75];
    for (const y of railYs) {
      if (horiz) {
        addBox(center, y, seg.fixed, segLen, railHeight, railThickness);
      } else {
        addBox(seg.fixed, y, center, railThickness, railHeight, segLen);
      }
    }
  }

  if (style === 'picket') {
    const pSpacing = 0.22;
    const pWidth = 0.06;
    const pHeight = fence.height * 0.92;
    const pCount = Math.max(1, Math.floor((segLen - 0.1) / pSpacing));
    const pStart = seg.lo + (segLen - pCount * pSpacing) / 2;
    for (let i = 0; i < pCount; i++) {
      const t = pStart + i * pSpacing + pSpacing / 2;
      if (horiz) {
        addBox(t, pHeight / 2, seg.fixed, pWidth, pHeight, 0.02);
      } else {
        addBox(seg.fixed, pHeight / 2, t, 0.02, pHeight, pWidth);
      }
    }
  }

  if (style === 'slat') {
    const slats = 6;
    const slatThick = 0.025;
    const gap = 0.04;
    const slatHeight = (fence.height - gap * (slats + 1)) / slats;
    for (let i = 0; i < slats; i++) {
      const y = gap + slatHeight / 2 + i * (slatHeight + gap);
      if (horiz) {
        addBox(center, y, seg.fixed, segLen, slatHeight, slatThick);
      } else {
        addBox(seg.fixed, y, center, slatThick, slatHeight, segLen);
      }
    }
  }
}

function buildHedge(
  group: Group,
  segments: Array<{ axis: 'x' | 'z'; lo: number; hi: number; fixed: number }>,
  height: number,
) {
  const hedgeMat = new MeshStandardMaterial({ color: new Color('#3b6a3a'), roughness: 0.95 });
  hedgeMat.name = 'HedgeMaterial';
  const hedgeWidth = 0.4;
  for (const seg of segments) {
    const segLen = seg.hi - seg.lo;
    if (segLen <= 0.05) continue;
    const center = (seg.lo + seg.hi) / 2;
    const horiz = seg.axis === 'x';
    const sx = horiz ? segLen : hedgeWidth;
    const sz = horiz ? hedgeWidth : segLen;
    const mesh = new Mesh(new BoxGeometry(sx, height, sz), hedgeMat);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    if (horiz) {
      mesh.position.set(center, height / 2, seg.fixed);
    } else {
      mesh.position.set(seg.fixed, height / 2, center);
    }
    group.add(mesh);
  }
  group.userData.disposeExtras = () => hedgeMat.dispose();
}

function dedupePositions(positions: Array<[number, number]>): Array<[number, number]> {
  const seen = new Set<string>();
  const out: Array<[number, number]> = [];
  for (const [x, z] of positions) {
    const key = `${x.toFixed(3)}|${z.toFixed(3)}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push([x, z]);
  }
  return out;
}
